import {
  Action,
  BoundAction,
  Listener,
  Store as IStore,
  Unsubscribe,
} from 'unistore';
import produce, { Draft } from 'immer';

import {
  BusinessDetails,
  CustomerDetails,
  Label,
  OrderDetails,
  PaymentMethodToken,
  PurchaseInformation,
  SupportedLocale,
  VaultInformation,
  VaultListItem,
} from '../types';
import { Scene } from '../enums/Checkout';
import { TranslationUnit } from '../assets/translations/Translations';
import { BasePaymentMethod } from '../payment-methods/BasePaymentMethod';
import { CardMetadata } from '../hosted-scripts/CardMetadata';

export interface NodeLike {
  id: string;
}

export enum SceneStage {
  Init = 'init',
  Mounting = 'mounting',
  Entering = 'entering',
  Entered = 'entered',
  Exiting = 'exiting',
  Exited = 'exited',
}

export type SceneState = {
  stage: SceneStage;
};

export interface ICheckoutState {
  isLoading: boolean;
  isProcessing: boolean;
  //
  locale: SupportedLocale;
  translation: TranslationUnit | null;
  tokens: PaymentMethodToken[];

  currentToken: PaymentMethodToken | null;

  apms: {
    selected: Nullable<string>;
    items: NodeLike[];
  };
  vault: {
    selected: Nullable<string>;
    items: VaultListItem[];
  };
  options: {
    purchaseInfo: PurchaseInformation | VaultInformation | null;
    customerDetails: CustomerDetails | null;
    businessDetails: BusinessDetails | null;
    orderDetails: OrderDetails | null;
    orderDetailsInitiator: string | null;
    showSavePaymentMethod: boolean;
  };

  ///////////////////////////////////////////
  // Current scene
  ///////////////////////////////////////////
  sceneStates: Partial<Record<Scene, SceneState>>;

  ///////////////////////////////////////////
  // Vaulted payment methods
  ///////////////////////////////////////////
  selectedVaultedPaymentMethod?: string;

  ///////////////////////////////////////////
  // Error
  ///////////////////////////////////////////
  error: {
    message: string | null;
  };

  ///////////////////////////////////////////
  // SmallPrint
  ///////////////////////////////////////////

  smallPrint: {
    message: string | null;
  };

  ///////////////////////////////////////////
  // Payment Methods
  ///////////////////////////////////////////
  paymentMethods: Record<string, BasePaymentMethod>;

  ///////////////////////////////////////////
  // Checkout Modules
  ///////////////////////////////////////////
  checkoutModules: any[];

  ///////////////////////////////////////////
  // Submit Button
  ///////////////////////////////////////////
  submitButton: {
    isDisabled: boolean;
    isVisible: boolean;
    message: string;
  };

  ///////////////////////////////////////////
  // Card Form
  ///////////////////////////////////////////
  form: {
    inputLabelsVisible: boolean;
  };

  ///////////////////////////////////////////
  // Direct Debit
  ///////////////////////////////////////////

  directDebit: {
    customerName: string | null;
    customerAddress: string | null;
    customerIban: string | null;
  };

  ///////////////////////////////////////////
  // Cards
  ///////////////////////////////////////////
  card: {
    metadata: CardMetadata;
    numberField: { error: string | null };
  };

  ///////////////////////////////////////////
  // Payment Method Popup Overlay
  ///////////////////////////////////////////
  paymentMethodPopupOverlay: {
    isVisible: boolean;

    paymentMethodName?: string;
    logoSrc?: string;
    logoAltLabel?: string;

    onCloseClick?: () => void;
    onFocusClick?: () => void;
  };
}

export class BaseStore<K = ICheckoutState> implements IStore<K> {
  readonly state: IStore<K>;

  constructor(state: IStore<K>) {
    this.state = state;
  }

  action(action: Action<K>): BoundAction {
    return this.state.action(action);
  }

  setState<U extends keyof K>(
    update: Pick<K, U>,
    overwrite?: boolean,
    action?: Action<K>,
  ): void {
    this.state.setState(update, overwrite, action);
  }

  produceState(recipe: (draft: Draft<K>) => Pick<K, never> | void): void {
    this.state.setState(produce(this.getState(), recipe));
  }

  subscribe(f: Listener<K>): Unsubscribe {
    return this.state.subscribe(f);
  }

  unsubscribe(f: Listener<K>): void {
    return this.state.unsubscribe(f);
  }

  getState(): K {
    return this.state.getState();
  }
}
